/**
 * Thai message catalog.
 *
 * Typed as Record<MessageKey, string>, so it defines every key in en.ts.
 */
import type { MessageKey } from './en';

export const th: Record<MessageKey, string> = {
  // Common
  'common.unknown': 'ไม่ทราบ',
  'common.tryAgain': 'ลองอีกครั้ง',
  'common.walletFallback': 'กระเป๋าเงิน',
  'common.loadingWallets': 'กำลังโหลดกระเป๋าเงินของคุณ...',
  'common.back': 'ย้อนกลับ',
  'common.close': 'ปิด',
  'common.loading': 'กำลังโหลด…',
  'common.select': 'เลือก',
  'common.selected': 'เลือกแล้ว',
  'common.beta': 'BETA',
  'common.cancel': 'ยกเลิก',
  'common.refresh': 'รีเฟรช',
  'common.refreshing': 'กำลังรีเฟรช',
  'common.all': 'ทั้งหมด',
  'common.wallets': 'กระเป๋าเงิน',
  'common.wallet': 'กระเป๋าเงิน',
  'common.networks': 'เครือข่าย',
  'common.network': 'เครือข่าย',
  'common.pending': 'รอดำเนินการ',
  'common.unavailable': 'ไม่พร้อมใช้งาน',
  'common.unverified': 'ยังไม่ยืนยัน',
  'common.settings': 'การตั้งค่า',
  'menu.title': 'เมนู',
  'menu.open': 'เปิดเมนู',
  'menu.close': 'ปิดเมนู',
  'common.tier.testnet': 'Testnet',
  'common.tier.mainnet': 'Mainnet',
  'common.theme.auto': 'อัตโนมัติ',
  'common.theme.light': 'สว่าง',
  'common.theme.dark': 'มืด',

  // Navigation
  'nav.primary': 'หลัก',
  'nav.home': 'หน้าหลัก',

  // Settings — page
  'settings.title': 'การตั้งค่า',

  // Settings — approvals section
  'settings.approvals.title': 'การอนุมัติ',
  'settings.approvals.hint': 'ตรวจสอบธุรกรรมที่รอการตัดสินใจจากคุณก่อนที่เกตเวย์จะลงนาม',
  'settings.approvals.link': 'ตรวจสอบรายการที่รออนุมัติ',

  // Settings — appearance section
  'settings.appearance.title': 'รูปลักษณ์',
  'settings.appearance.hint': 'เลือกรูปแบบการแสดงผลของแอป',
  'settings.appearance.themeGroup': 'ธีม',

  // Settings — language section
  'preauth.menu.title': 'ภาษาและรูปลักษณ์',
  'preauth.menu.open': 'เปิดเมนูภาษาและรูปลักษณ์',
  'settings.language.title': 'ภาษา',
  'settings.language.hint': 'เลือกภาษาของคุณ',

  // Settings — developer mode section
  'settings.devmode.title': 'โหมดนักพัฒนา',
  'settings.devmode.label': 'โหมดนักพัฒนา',
  'settings.devmode.desc': 'เปิดเพื่อใช้ Testnet ปิดเพื่อใช้ Mainnet',
  'settings.devmode.note': 'โหมด Testnet ยอดคงเหลือไม่มีมูลค่าจริง',

  // Settings — deployment section
  'settings.deployment.title': 'การปรับใช้',
  'settings.deployment.network': 'เครือข่าย',
  'settings.deployment.gatewayEnv': 'สภาพแวดล้อมเกตเวย์',
  'settings.deployment.bitcoinNetwork': 'เครือข่าย Bitcoin',
  'settings.deployment.organization': 'องค์กร',

  // Settings — security section (device passkeys)
  'settings.security.title': 'ความปลอดภัย',
  'settings.security.hint': 'เพิ่มพาสคีย์ของอุปกรณ์เพื่ออนุมัติธุรกรรมบนอุปกรณ์นี้ก่อนที่เกตเวย์จะลงนาม',
  'settings.security.methodsTitle': 'วิธียืนยันตัวตน',
  'settings.security.addDevice': 'เพิ่มวิธียืนยันตัวตน',
  'settings.security.limitReached': 'ลงทะเบียนวิธียืนยันตัวตนได้สูงสุด {max} วิธี',
  'settings.security.adding': 'กำลังรออุปกรณ์…',
  'settings.security.remove': 'นำออก',
  'settings.security.removing': 'กำลังนำออก…',
  'settings.security.empty': 'ยังไม่มีพาสคีย์ของอุปกรณ์ที่ลงทะเบียน',
  'settings.security.loading': 'กำลังโหลดวิธียืนยันตัวตน…',
  'settings.security.retry': 'ลองอีกครั้ง',
  'settings.security.unsupported': 'เบราว์เซอร์นี้ไม่รองรับพาสคีย์ของอุปกรณ์',
  'settings.security.enrolledOn': 'เพิ่มเมื่อ {date}',
  'settings.security.lastUsed': 'ใช้ล่าสุดเมื่อ {date}',
  'settings.security.deviceFallbackLabel': 'พาสคีย์ของอุปกรณ์',

  // Create-a-passkey prompt (one-time, after a fresh OAuth or OTP login)
  'passkey.create.title': 'สร้างพาสคีย์',
  'passkey.create.body': 'เข้าสู่ระบบด้วยลายนิ้วมือ ใบหน้า หรือการล็อกหน้าจอ โดยไม่ต้องจำรหัสผ่าน',
  'passkey.create.cta': 'สร้างพาสคีย์',
  'passkey.create.creating': 'กำลังรออุปกรณ์ของคุณ…',
  'passkey.create.dismiss': 'ไม่ต้องแสดงอีก',
  'passkey.create.error': 'สร้างพาสคีย์ไม่ได้ โปรดลองอีกครั้งหรือเพิ่มภายหลังจากความปลอดภัยในเมนู',
  'passkey.stateUnknown':
    'การโอนไม่ได้ดำเนินการ เพราะปัญหาการเชื่อมต่อทำให้ยืนยันวิธียืนยันตัวตนของคุณไม่ได้ โปรดลองอีกครั้ง',

  // Settings — account section
  'settings.account.title': 'บัญชี',
  'settings.account.signout': 'ออกจากระบบ',

  // Login — page
  'login.title': 'ไปยังกระเป๋าเงินของคุณ',
  'login.eyebrow': 'ยินดีต้อนรับ',
  'login.error.start': 'เริ่มเข้าสู่ระบบไม่ได้ โปรดตรวจสอบการเชื่อมต่อแล้วลองอีกครั้ง',
  'login.divider': 'หรือ',
  'login.more': 'วิธีเข้าสู่ระบบเพิ่มเติม',

  // Login — providers ({name} is the brand for the fallback label)
  'login.provider.google': 'ดำเนินการต่อด้วย Google',
  'login.provider.apple': 'ดำเนินการต่อด้วย Apple',
  'login.provider.discord': 'ดำเนินการต่อด้วย Discord',
  'login.provider.twitter': 'ดำเนินการต่อด้วย X',
  'login.provider.kakao': 'ดำเนินการต่อด้วย Kakao',
  'login.provider.fallback': 'ดำเนินการต่อด้วย {name}',
  'login.provider.unavailable': 'วิธีเข้าสู่ระบบนี้ไม่พร้อมใช้งานในขณะนี้',

  // Login — passkey sign-in
  'login.passkey.button': 'เข้าสู่ระบบอย่างปลอดภัย',
  'login.passkey.desc': 'ใช้วิธียืนยันตัวตนที่ลงทะเบียนไว้ในอุปกรณ์ของคุณ เช่น ใบหน้าหรือลายนิ้วมือ',
  'login.passkey.pending': 'กำลังรอพาสคีย์…',
  'login.passkey.cancelled':
    'การเข้าสู่ระบบด้วยพาสคีย์ถูกยกเลิก หรืออุปกรณ์นี้ไม่มีพาสคีย์สำหรับเว็บไซต์นี้ โปรดลองอีกครั้งหรือเลือกวิธีอื่น',
  'login.passkey.error': 'เข้าสู่ระบบด้วยพาสคีย์ไม่ได้ โปรดลองอีกครั้งหรือเลือกวิธีอื่น',
  // Shown when sign-in found no passkey or no account. Passkey creation lives in the
  // low-weight "create an account with a passkey" link below, so this points there.
  'login.passkey.noAccount': 'ไม่พบพาสคีย์บนอุปกรณ์นี้ โปรดใช้ “สร้างกระเป๋าเงิน” ด้านล่างเพื่อสร้างพาสคีย์',

  // Login — passkey signup (a low-weight link below sign-in for a new user)
  'login.signup.prompt': 'เพิ่งเริ่มใช้งานใช่ไหม',
  'login.signup.link': 'สร้างกระเป๋าเงิน',
  'login.signup.pending': 'กำลังสร้างบัญชี…',
  'login.signup.exists': 'คุณมีบัญชีอยู่แล้ว โปรดเข้าสู่ระบบด้วยพาสคีย์',
  'login.signup.error': 'สร้างบัญชีไม่ได้ โปรดลองอีกครั้งหรือเลือกวิธีอื่น',

  // Login — organization escape hatch (inside the "more ways to sign in" section)
  'login.org.label': 'ID องค์กร',
  'login.org.note': 'เว้นว่างไว้เพื่อเข้าสู่พื้นที่ส่วนตัวของคุณ แล้วเลือกวิธีเข้าสู่ระบบด้านบน',

  // Login — developer fallback
  'login.dev.body': 'สำหรับการพัฒนาในเครื่องกับเกตเวย์ ให้ตั้งค่าโทเค็นจากคอนโซลของเบราว์เซอร์:',

  // Dashboard (home)
  'dashboard.offline': 'ดูเหมือนว่าคุณออฟไลน์อยู่ โปรดเชื่อมต่อใหม่เพื่อโหลดกระเป๋าเงิน',
  'dashboard.empty': 'ยังไม่มีกระเป๋าเงิน สร้างกระเป๋าเงินเพื่อเริ่มต้น',

  // Home components
  'home.account.allWallets': 'กระเป๋าเงินทั้งหมด',
  'home.account.subtitle': '{wallets} {walletWord} · {networks} {networkWord}',
  'home.switcher.aria': 'สลับบัญชี ปัจจุบันคือ {name}',
  'home.copy.aria': 'คัดลอกที่อยู่ {name}',
  'home.portfolio.aria': 'ยอดคงเหลือพอร์ตโฟลิโอ',
  'home.portfolio.total': 'ยอดคงเหลือรวม',
  'home.actions.aria': 'การดำเนินการกระเป๋าเงิน',
  'home.action.swapUnavailable': 'ฟีเจอร์สวอปยังไม่พร้อมใช้งาน',
  'home.action.buySellUnavailable': 'ฟีเจอร์ซื้อขายยังไม่พร้อมใช้งาน',
  'home.action.buySell': 'ซื้อ/ขาย',
  'home.network.testnetTitle': 'สภาพแวดล้อม Testnet ยอดคงเหลือที่แสดงไม่มีมูลค่าจริง',
  'home.network.mainnetTitle': 'Mainnet ยอดคงเหลืออ้างอิงจากเครือข่ายจริง',

  // App navigation / account-wide surfaces
  'activity.title': 'กิจกรรม',
  'activity.history': 'ประวัติ',

  // Transaction list
  'tx.aria.history': 'ประวัติธุรกรรม',
  'tx.date.pending': 'รอดำเนินการ',
  'tx.direction.receive': 'รับ',
  'tx.direction.send': 'ส่ง',
  'tx.direction.swap': 'สวอป',
  'tx.relation.from': 'จาก',
  'tx.relation.to': 'ถึง',
  'tx.swapDetails.aria': 'รายละเอียดสวอป: จาก {from} เป็น {to}',
  'tx.status.pending': 'รอดำเนินการ',
  'tx.status.pending_approval': 'รออนุมัติ',
  'tx.status.pending_policy': 'กำลังตรวจสอบนโยบาย',
  'tx.status.signing': 'กำลังลงนาม',
  'tx.status.signed': 'ลงนามแล้ว',
  'tx.status.submitted': 'ส่งแล้ว',
  'tx.status.broadcast': 'เผยแพร่แล้ว',
  'tx.status.confirmed': 'ยืนยันแล้ว',
  'tx.status.failed': 'ล้มเหลว',
  'tx.status.rejected': 'ปฏิเสธแล้ว',
  'tx.status.unknown': 'ไม่ทราบ',
  'tx.loading': 'กำลังโหลดกิจกรรม…',
  'tx.empty': 'ยังไม่มีกิจกรรม',
  'tx.empty.hint': 'รายการส่งและสวอปของคุณจะแสดงที่นี่',
  'tx.loadingMore': 'กำลังโหลดเพิ่มเติม…',
  'tx.explorer.aria': 'ดูบนตัวสำรวจบล็อกเชน',

  // Approvals
  'approvals.title': 'การอนุมัติ',
  'approvals.error.load': 'โหลดรายการอนุมัติไม่ได้',
  'approvals.empty.title': 'คุณจัดการครบแล้ว',
  'approvals.empty.body': 'ไม่มีธุรกรรมที่รอการตัดสินใจ คำขอใหม่ที่ต้องให้คุณตรวจสอบจะแสดงที่นี่',
  'approvals.wallet.title': 'รายการอนุมัติของกระเป๋าเงินนี้',
  'approvals.wallet.resolving': 'กำลังตรวจสอบว่ารายการอนุมัติใดเป็นของกระเป๋าเงินนี้…',
  'approvals.wallet.empty.title': 'กระเป๋าเงินนี้ไม่มีรายการรออนุมัติ',
  'approvals.wallet.empty.body': 'ไม่มีธุรกรรมของกระเป๋าเงินนี้ที่รอการตัดสินใจ คำขอที่ต้องให้คุณตรวจสอบจะแสดงที่นี่',
  'approvals.status.pending': 'รอดำเนินการ',
  'approvals.status.approved': 'อนุมัติแล้ว',
  'approvals.status.rejected': 'ปฏิเสธแล้ว',
  'approvals.status.expired': 'หมดอายุแล้ว',
  'approvals.quorum': 'อนุมัติแล้ว {received} จาก {required}',
  'approvals.quorum.aria': 'ความคืบหน้าของจำนวนอนุมัติขั้นต่ำ',
  'approvals.waiting.more': 'รออีก {count} {word}',
  'approvals.waiting.approval': 'การอนุมัติ',
  'approvals.waiting.approvals': 'การอนุมัติ',
  'approvals.waiting.signing': 'กำลังรอให้กระบวนการลงนามเสร็จสิ้น',
  'approvals.timeline.checker': 'ผู้ตรวจสอบ',
  'approvals.timeline.acted': 'ดำเนินการแล้ว',
  'approvals.detail.loading': 'กำลังโหลดธุรกรรมที่ถูกต้อง…',
  'approvals.detail.unavailable': 'โหลดรายละเอียดธุรกรรมไม่ได้ โปรดอนุมัติหรือปฏิเสธเมื่อคุณยืนยันการโอนนี้ด้วยวิธีอื่นได้เท่านั้น',
  'approvals.detail.notProvided': 'ไม่มีข้อมูลในบันทึกธุรกรรม',
  'approvals.detail.requestedBy': 'ผู้ร้องขอ',
  'approvals.detail.transactionStatus': 'สถานะธุรกรรม',
  'approvals.summary.transaction': 'ธุรกรรม',
  'approvals.action.busy': 'กำลังประสานการลงนามแบบหลายฝ่าย เมื่อได้จำนวนอนุมัติขั้นต่ำ เกตเวย์จะลงนามและเผยแพร่ก่อนตอบกลับ ซึ่งอาจใช้เวลาสองสามวินาที',
  'approvals.action.approve': 'อนุมัติ',
  'approvals.action.reject': 'ปฏิเสธ',
  'approvals.confirm.approveTitle': 'อนุมัติธุรกรรมนี้หรือไม่',
  'approvals.confirm.rejectTitle': 'ปฏิเสธธุรกรรมนี้หรือไม่',
  'approvals.confirm.approveConsequence':
    'การอนุมัติของคุณจะถูกส่งไปยังเกตเวย์ เมื่อได้จำนวนอนุมัติขั้นต่ำ เกตเวย์จะลงนามและเผยแพร่ธุรกรรมนี้ก่อนตอบกลับ ซึ่งอาจใช้เวลาสองสามวินาที โปรดอย่าปิดแอประหว่างดำเนินการ',
  'approvals.confirm.rejectConsequence': 'การดำเนินการนี้จะหยุดการลงนามธุรกรรม ทำให้ไม่สามารถได้จำนวนอนุมัติขั้นต่ำและธุรกรรมจะไม่ถูกเผยแพร่',
  'approvals.confirm.reasonLabel': 'เหตุผล (ไม่บังคับ)',
  'approvals.confirm.reasonPlaceholder': 'เพิ่มบันทึกสำหรับหลักฐานการตรวจสอบ',
  'approvals.confirm.approving': 'กำลังอนุมัติ',
  'approvals.confirm.rejecting': 'กำลังปฏิเสธ',
  'approvals.section.pending': 'ต้องให้คุณตรวจสอบ ({count})',
  'approvals.section.decided': 'ตัดสินใจแล้ว',

  // Create wallet
  'create.title': 'สร้างกระเป๋าเงิน',
  'create.lead': 'เลือกประเภทและชื่อกระเป๋าเงิน การสร้างคีย์จะดำเนินการอย่างปลอดภัยบนเซิร์ฟเวอร์ อุปกรณ์ของคุณไม่เก็บคีย์และไม่ลงนามที่นี่',
  'create.walletName': 'ชื่อกระเป๋าเงิน',
  'create.walletPlaceholder': 'กระเป๋าเงินของฉัน',
  'create.walletType': 'ประเภทกระเป๋าเงิน',
  'create.kind.evm.label': 'บัญชี EVM',
  'create.kind.evm.desc': 'Ethereum · Base · BSC · HyperEVM',
  'create.kind.tron.label': 'Tron',
  'create.kind.tron.desc': 'TRX และโทเคน TRC-20',
  'create.kind.solana.label': 'Solana',
  'create.kind.solana.desc': 'SOL และโทเคน SPL',
  'create.kind.sui.label': 'Sui',
  'create.kind.sui.desc': 'SUI และโทเคน Sui',
  'create.kind.bitcoin.label': 'Bitcoin',
  'create.kind.bitcoin.desc': 'BTC · ที่อยู่ SegWit',

  'provisioning.title': 'กำลังสร้างกระเป๋าเงินของคุณ',
  'provisioning.unavailable': 'ไม่รองรับที่นี่',
  'walletSettings.statusFrozen': 'ถูกระงับ',
  'dashboard.stillCreating': 'กำลังสร้างกระเป๋าเงินอีก {count} ใบ',
  'dashboard.createWallet': 'สร้างกระเป๋าเงิน',
  'provisioning.failed': 'ไม่สามารถสร้างกระเป๋าเงินของคุณโดยอัตโนมัติได้',
  'provisioning.createManually': 'สร้างกระเป๋าเงินด้วยตนเอง',
  'provisioning.succeeded': 'กระเป๋าเงินของคุณพร้อมแล้ว',
  'provisioning.partial':
    'สร้างกระเป๋าเงินสำหรับ {created} สำเร็จแล้ว แต่ไม่สามารถสร้างกระเป๋าเงินสำหรับ {missing} ได้',
  'provisioning.partialHint': 'กระเป๋าเงินที่ยังไม่ได้สร้างสามารถเพิ่มด้วยตนเองได้ในภายหลัง',
  'provisioning.pending': 'ระบบยังสร้างกระเป๋าเงินของคุณอยู่',
  'provisioning.pendingHint': 'อีกสักครู่ กระเป๋าเงินจะปรากฏบนหน้าหลักโดยอัตโนมัติ',
  'provisioning.goHome': 'ดำเนินการต่อ',
  'create.busy': 'กำลังดำเนินกระบวนการสร้างคีย์',
  'create.toast.created': 'สร้างกระเป๋าเงินแล้ว',

  // OAuth callback
  'oauth.pending.title': 'กำลังเข้าสู่ระบบ',
  'oauth.pending.message': 'กำลังดำเนินการเข้าสู่ระบบให้เสร็จสิ้น โปรดรอสักครู่',
  'oauth.failure.title': 'เข้าสู่ระบบไม่สำเร็จ',
  'oauth.failure.fallback': 'ดำเนินการเข้าสู่ระบบให้เสร็จสิ้นไม่ได้',
  'oauth.backToSignIn': 'กลับไปเข้าสู่ระบบ',

  // Receive ({chain} is the resolved chain name)
  'receive.title': 'รับ',
  'receive.subtitle': 'เลือกเครือข่ายและที่อยู่สำหรับรับ',
  'receive.chain.fallback': 'กระเป๋าเงินนี้',
  'receive.chainSelector.aria': 'เลือกเครือข่ายสำหรับรับ',
  'receive.addressPicker.label': 'ที่อยู่',
  'receive.addressPending': 'กำลังรอที่อยู่',
  'receive.qr.title': 'คิวอาร์โค้ดสำหรับที่อยู่ {chain}',
  'receive.qr.unavailable': 'คิวอาร์โค้ดไม่พร้อมใช้งาน โปรดคัดลอกที่อยู่ด้านล่าง',
  'receive.addressLabel': 'ที่อยู่ {chain}',
  'receive.warning': 'ส่งเฉพาะสินทรัพย์บน {chain} มายังที่อยู่นี้',
  'receive.copy.copy': 'คัดลอกที่อยู่',
  'receive.copy.copied': 'คัดลอกที่อยู่แล้ว',
  'receive.copy.failed': 'คัดลอกไม่สำเร็จ',
  'receive.copy.aria': 'คัดลอกที่อยู่ไปยังคลิปบอร์ด',
  'receive.pending.setup': 'กระเป๋าเงินนี้ยังอยู่ระหว่างตั้งค่า ที่อยู่จะแสดงเมื่อพร้อมใช้งาน',
  'receive.pending.loading': 'กำลังโหลดที่อยู่ของกระเป๋าเงินนี้...',
  'receive.empty': 'ยังไม่มีกระเป๋าเงินสำหรับรับสินทรัพย์',

  // Send ({asset}, {chain}, {label}, {reason} are interpolated at render)
  'send.title': 'ส่ง',
  'send.selectAsset': 'เลือกสินทรัพย์ที่จะส่ง',
  'send.loadingAssets': 'กำลังโหลดสินทรัพย์ของคุณ...',
  'send.emptyAssets': 'ยังไม่มีสินทรัพย์ที่มียอดคงเหลือพร้อมใช้ โปรดรับสินทรัพย์ก่อนส่ง',
  'send.emptyNoWallets': 'ยังไม่มีกระเป๋าเงิน โปรดสร้างกระเป๋าเงินเพื่อใช้ส่ง',
  'send.backToAssets': 'กลับไปยังสินทรัพย์',
  'send.stepTwo.title': 'ส่ง {asset} บนเครือข่าย {chain}',
  'send.context.none': 'ยังไม่ได้เลือกกระเป๋าเงิน',
  'send.context.from': 'จาก {label} บน {chain}',
  'send.context.fromUnsupported': 'จาก {label} {reason}',
  'send.error.wrongTier': 'กระเป๋าเงินนี้ไม่ได้อยู่บนเครือข่ายที่ใช้งาน โปรดสลับเครือข่ายหรือเลือกกระเป๋าเงินบนเครือข่ายปัจจุบันก่อนส่ง',
  'send.error.noWallet': 'เลือกกระเป๋าเงินที่จะใช้ส่ง',
  'send.error.chainUnavailable': 'ไม่สามารถส่งบนเครือข่ายนี้ได้',
  'send.status.submitting': 'กำลังส่งธุรกรรม',
  'send.status.buildingPayload': 'กำลังอ่านค่าธรรมเนียมเครือข่ายและ nonce',
  'send.status.pendingPolicy': 'การโอนนี้ต้องได้รับการอนุมัติเพิ่มเติมหรือถูกล็อกเวลาตามนโยบาย และจะยังไม่ดำเนินการจนกว่าจะเป็นไปตามเงื่อนไข',
  'send.status.noApproval': 'สร้างธุรกรรมแล้ว แต่ไม่สามารถดำเนินการอนุมัติที่นี่ได้ โปรดไปที่หน้าการอนุมัติเพื่อดำเนินการต่อ',
  'send.confirm.title': 'ยืนยันการส่ง',
  'send.confirm.note': 'ตรวจสอบที่อยู่ปลายทางให้รอบคอบ การโอนสินทรัพย์ไม่สามารถย้อนกลับได้',
  'send.confirm.to': 'ถึง',
  'send.confirm.amount': 'จำนวน',
  'send.confirm.network': 'เครือข่าย',
  'send.confirm.networkFee': 'ค่าธรรมเนียมเครือข่าย',
  'send.confirm.networkFeeValue': 'เกตเวย์จะประมาณและนำไปใช้เมื่อสร้างธุรกรรม',
  'send.confirm.networkFeeValueEvm': 'อ่านจากเครือข่ายเมื่อคุณยืนยัน แล้วนำไปใช้กับธุรกรรม',
  'send.confirm.submit': 'ยืนยันและส่ง',
  'send.confirm.sending': 'กำลังส่ง',
  'send.status.title': 'สถานะการส่ง',
  'send.status.statusLabel': 'สถานะ',
  'send.status.txId': 'ID ธุรกรรม',
  'send.status.hash': 'แฮช',
  'send.status.explorer': 'ดูบนตัวสำรวจบล็อกเชน',
  'send.status.goToApprovals': 'ไปยังการอนุมัติ',
  'send.status.backToSend': 'กลับไปหน้าส่ง',
  'send.result.status': 'การโอน{status}',
  'send.result.statusReason': 'การโอน{status}{reason}',

  // Send form and asset picker
  'send.form.recipient': 'ที่อยู่ผู้รับ',
  'send.form.destinationPlaceholder': 'ที่อยู่ปลายทาง',
  'send.form.noWallet': 'เลือกกระเป๋าเงินที่จะใช้ส่ง',
  'send.form.chainUnavailable': 'ไม่สามารถส่งบนเครือข่ายนี้จากที่นี่ได้',
  'send.validation.recipientRequired': 'กรอกที่อยู่ผู้รับ',
  'send.validation.evmWebUnavailable': 'ไม่สามารถส่งบนเครือข่ายนี้ผ่านเว็บแอปได้',
  'send.validation.evmNoNetwork': 'เลือกเหรียญที่ต้องการส่งเพื่อระบุเครือข่าย บัญชีนี้ใช้ที่อยู่เดียวกันบนทุกเครือข่าย EVM',
  'send.validation.evmTokenUnsupported': 'เว็บแอปยังไม่รองรับการส่งโทเคนบนเครือข่าย EVM กรุณาใช้แอปมือถือสำหรับโอนโทเคน หรือส่งเหรียญหลักของเครือข่ายที่นี่',
  'send.validation.evmImportedTokenUnsupported': 'ไม่สามารถส่งโทเคนที่นำเข้าจากเว็บแอปได้ ส่งได้เฉพาะโทเคนที่ผ่านการยืนยันในทะเบียนและเหรียญของเครือข่ายเองเท่านั้น',
  'send.validation.evmTokenDecimalsUnknown': 'ไม่สามารถระบุความละเอียดของโทเคนนี้ได้ จึงไม่สามารถเข้ารหัสการโอนอย่างปลอดภัย ยังไม่มีการลงนามใด ๆ',
  'send.validation.evmPrefix': 'ที่อยู่ EVM ขึ้นต้นด้วย 0x',
  'send.validation.evmFormat': 'ที่อยู่ EVM คือ 0x ตามด้วยอักขระฐานสิบหก 40 ตัว',
  'send.validation.evmChecksum': 'ที่อยู่ EVM นี้มีเช็คซัมไม่ถูกต้อง',
  'send.validation.evmZeroAddress': 'นี่คือที่อยู่ศูนย์ สินทรัพย์ที่ส่งไปจะไม่สามารถกู้คืนได้',
  'send.validation.suiPrefix': 'ที่อยู่ Sui ขึ้นต้นด้วย 0x',
  'send.validation.suiFormat': 'ที่อยู่ Sui คือ 0x ตามด้วยอักขระเลขฐานสิบหกตัวพิมพ์เล็ก 64 ตัว',
  'send.validation.tronPrefix': 'ที่อยู่ Tron ขึ้นต้นด้วย T',
  'send.validation.tronLength': 'ที่อยู่ Tron มี 34 อักขระ',
  'send.validation.tronChecksum': 'ที่อยู่ Tron นี้มี checksum ไม่ถูกต้อง',
  'send.validation.tronInvalid': 'ที่อยู่นี้ไม่ใช่ที่อยู่ Tron ที่ถูกต้อง',
  'send.validation.solanaInvalid': 'ที่อยู่นี้ดูไม่เหมือนที่อยู่ Solana',
  'send.validation.solanaBytes': 'ที่อยู่ Solana เมื่อถอดรหัสแล้วต้องมี 32 ไบต์',
  'send.validation.bitcoinChecksum': 'ที่อยู่ Bitcoin นี้มี checksum ไม่ถูกต้อง',
  'send.validation.bitcoinInvalid': 'ที่อยู่นี้ไม่ใช่ที่อยู่ Bitcoin ที่ถูกต้อง',
  'send.validation.bitcoinSegwitInvalid': 'ที่อยู่นี้ไม่ใช่ที่อยู่ SegWit ที่ถูกต้อง',
  'send.validation.bitcoinTaprootInvalid': 'ที่อยู่นี้ไม่ใช่ที่อยู่ Taproot ที่ถูกต้อง',
  'send.validation.bitcoinWrongNetwork': 'ที่อยู่นี้ใช้สำหรับเครือข่าย Bitcoin อื่น (ต้องเป็น {expected})',
  'send.validation.bitcoinDust': 'จำนวนต่ำกว่าขีดจำกัด dust ของ Bitcoin ที่ {sats} sats',
  'send.form.amountRequired': 'กรอกจำนวน',
  'send.form.numberRequired': 'กรอกตัวเลข',
  'send.form.amountPositive': 'จำนวนต้องมากกว่าศูนย์',
  'send.form.decimals': '{symbol} รองรับทศนิยมสูงสุด {decimals} ตำแหน่ง',
  'send.form.exceedsBalance': 'จำนวนเกินยอดคงเหลือ {symbol} ของคุณ',
  'send.form.assetAria': 'สินทรัพย์',
  'send.form.noAssets': 'ไม่มีสินทรัพย์',
  'send.form.available': 'ใช้ได้: {amount} {symbol}',
  'send.form.review': 'ตรวจสอบการส่ง',
  'send.form.clear': 'ล้าง',
  'send.assets.searchLabel': 'ค้นหาสินทรัพย์',
  'send.assets.searchPlaceholder': 'ค้นหาตามชื่อหรือสัญลักษณ์',
  'send.assets.empty': 'ไม่พบสินทรัพย์ที่ตรงกัน ล้างการค้นหาหรือตัวกรองเพื่อดูสินทรัพย์ที่คุณถืออยู่',

  // Swap ({decimals}, {provider}, {count} are interpolated at render)
  'swap.title': 'สวอป',
  'swap.subtitle': 'เส้นทางที่ดีที่สุดจากผู้ให้บริการทั้งหมด',
  'swap.balance': 'ยอดคงเหลือ {amount} {symbol}',
  'swap.max': 'สูงสุด',
  'swap.signer.overridden': '{selected} สลับไม่ได้ จึงใช้ {label} · {address} แทน',
  'swap.empty.evm': 'การสวอปต้องใช้กระเป๋าเงินที่มีที่อยู่ EVM โปรดสร้างจากหน้าหลักก่อน',
  'swap.error.loadChains': 'โหลดเครือข่ายไม่สำเร็จ',
  'swap.error.quote': 'ขอราคาไม่สำเร็จ',
  'swap.error.executeNotEnabled': 'บริการนี้ยังไม่ได้เปิดใช้การสวอป (ปิดตัวลงนามสำหรับการพัฒนา)',
  'swap.error.executeFallback': 'สวอปไม่สำเร็จ',
  'swap.side.from': 'คุณจ่าย',
  'swap.side.to': 'คุณได้รับ',
  'swap.chain.fromAria': 'เครือข่ายต้นทาง',
  'swap.chain.toAria': 'เครือข่ายปลายทาง',
  'swap.amount.fromAria': 'จำนวนที่จะส่ง',
  'swap.amount.toAria': 'จำนวนที่จะได้รับ',
  'swap.amount.invalidHint': 'กรอกจำนวนที่เป็นบวกและมีทศนิยมไม่เกิน {decimals} ตำแหน่ง',
  'swap.flip.aria': 'สลับทิศทางการสวอป',
  'swap.sameTokenHint': 'เลือกโทเค็นสองรายการที่ต่างกันเพื่อสวอป',
  'swap.warning.insufficientBalance': 'ยอดคงเหลือของคุณต่ำกว่าจำนวนนี้ การสวอปจึงจะล้มเหลว',
  'swap.warning.insufficientAllowance': 'ต้องอนุมัติโทเค็นก่อนดำเนินการสวอปนี้',
  'swap.toast.submitted': 'ส่งคำขอสวอปผ่าน {provider} แล้ว ติดตามได้ในกิจกรรม',
  'swap.confirm.swapping': 'กำลังสวอป…',
  'swap.confirm.via': 'สวอปผ่าน {provider}',
  'swap.confirm.summary': 'กำลังสลับ {inAmount} {inSymbol} เป็นประมาณ {outAmount} {outSymbol} ผ่าน {provider} การชำระจะเสร็จสิ้นแบบอะซิงโครนัสบนเครือข่ายปลายทาง',
  'swap.mpcNote': 'ลงนามโดยกระเป๋าของคุณผ่านขั้นตอนการลงนาม MPC ธุรกรรมต้นทางจะถูกกระจายบนเชน และผู้ให้บริการเป็นผู้ชำระการเติมเต็ม',
  'swap.confirm.default': 'สวอป',
  'swap.confirm.disabledTitle': 'การสวอปจะพร้อมใช้งานพร้อมการเชื่อมต่อการลงนามผ่านเกตเวย์',
  'swap.mock.enabledHint': 'ลงนามโดยกระเป๋าเงินสำหรับการพัฒนา (MPC จำลอง) · สวอปบนเชนจริง',
  'swap.mock.disabledHint': 'ราคาแบบเรียลไทม์ · การสวอปจะพร้อมใช้งานพร้อมขั้นตอนการลงนามผ่านเกตเวย์',
  'swap.picker.tokenAria': 'เลือกโทเค็น',
  'swap.picker.chainAria': 'เลือกเครือข่าย',
  'swap.picker.searchToken': 'ค้นหาชื่อ สัญลักษณ์ หรือที่อยู่',
  'swap.picker.searchChain': 'ค้นหากระเป๋า',
  'swap.picker.loadingTokens': 'กำลังโหลดโทเค็น…',
  'swap.picker.noTokens': 'ไม่พบโทเค็นที่ตรงกัน',
  'swap.picker.noChains': 'ไม่พบเครือข่ายที่ตรงกัน',
  'swap.picker.noWallets': 'ไม่มีกระเป๋าที่รับได้บนเชนที่รองรับ',
  'swap.picker.routes': '{count} เส้นทาง',
  'swap.token.aria': 'เลือกโทเค็น',
  'swap.quote.noRoute': 'ไม่มีเส้นทาง',
  'swap.quote.best': 'ดีที่สุด',
  'swap.quote.networkCost': 'ค่าใช้จ่ายเครือข่าย',
  'swap.quote.platformFee': 'ค่าธรรมเนียมแพลตฟอร์ม',
  'swap.quote.priceImpact': 'ผลกระทบต่อราคา',
  'swap.quote.time': 'เวลา',

  // Swap detail sheet
  'swap.detail.aria': 'รายละเอียดสวอป',
  'swap.detail.closeAria': 'ปิดรายละเอียด',
  'swap.detail.status': 'สถานะ',
  'swap.detail.paid': 'คุณจ่าย',
  'swap.detail.received': 'คุณได้รับ',
  'swap.detail.rate': 'อัตรา',
  'swap.detail.route': 'เส้นทาง',
  'swap.detail.provider': 'ผู้ให้บริการ',
  'swap.detail.feeQuoted': 'ค่าธรรมเนียม (เสนอราคา)',
  'swap.detail.feeCharged': 'ค่าธรรมเนียม (เรียกเก็บ)',
  'swap.detail.sourceTx': 'ธุรกรรมต้นทาง',
  'swap.detail.destinationTx': 'ธุรกรรมปลายทาง',
  'swap.origin.unreported': 'กำลังส่งธุรกรรม — ยังไม่ทราบผลลัพธ์',
  'swap.origin.reported': 'กำลังตรวจสอบเครือข่ายเพื่อค้นหาธุรกรรมของคุณ',
  'swap.origin.verified': 'ยืนยันการส่งบนเชนต้นทางแล้ว — กำลังรอเชนปลายทาง',
  'swap.origin.reverted': 'ธุรกรรมดำเนินการบนเชนแล้วแต่ล้มเหลว ไม่มีเงินถูกโอน',
  'swap.origin.unknown': 'ไม่สามารถยืนยันธุรกรรมนี้ได้ โปรดตรวจสอบประวัติกระเป๋าเงินก่อนลองอีกครั้ง',
  'swap.origin.noRetry': 'ยังลองใหม่ไม่ได้จนกว่าจะยืนยันธุรกรรมนี้หรือระบุได้ว่าธุรกรรมไม่เกิดขึ้น',
  'swap.origin.ackPrompt': 'สวอปนี้ยังไม่ได้รับการยืนยัน โปรดตรวจสอบประวัติกระเป๋าเงินก่อนปลดการระงับ',
  'swap.origin.ackRelease': 'ฉันตรวจสอบประวัติกระเป๋าเงินแล้ว — ปลดการระงับนี้',
  'swap.origin.trackingFailed':
    'ไม่สามารถบันทึกสวอปนี้ได้ จึงไม่ได้เริ่มสวอป ไม่มีการลงนามและไม่มีเงินถูกโอน',

  // Earn (lending market; {symbol}, {amount}, {usd}, {digest}, {reason} interpolated)
  'earn.title': 'ให้กู้และยืม',
  'earn.subtitle': 'ใช้สินทรัพย์ที่มีเป็นหลักประกันเพื่อยืมเงิน หรือให้กู้เพื่อรับผลตอบแทนและเพิ่มศักยภาพการลงทุน',
  'earn.error.market': 'ตลาดไม่พร้อมใช้งาน: {message}',
  'earn.error.retry': 'ลองใหม่',
  'earn.loading': 'กำลังโหลดตลาดให้กู้ยืม…',
  'earn.empty': 'ขณะนี้ไม่มีตลาดให้กู้ยืมที่พร้อมใช้งาน',
  'earn.table.asset': 'สินทรัพย์',
  'earn.table.totalSupply': 'ยอดฝากรวม',
  'earn.table.totalBorrow': 'ยอดยืมรวม',
  'earn.table.ltv': 'LTV',
  'earn.table.aria': 'ตลาดให้กู้ยืม',
  'earn.vault.title': 'Vault {symbol}',
  'earn.vault.supplyYield': 'ผลตอบแทนจากการฝาก',
  'earn.vault.borrowInterest': 'ดอกเบี้ยการยืม',
  'earn.vault.marketPrice': 'ราคาตลาด',
  'earn.vault.utilization': 'อัตราการใช้งาน',
  'earn.vault.depositThreshold': 'เกณฑ์การฝาก',
  'earn.vault.borrowThreshold': 'เกณฑ์การยืม',
  'earn.vault.borrowFee': 'ค่าธรรมเนียมการยืม: {pct}%',
  'earn.account.title': 'บัญชีของคุณ',
  'earn.account.deposits': 'เงินฝากของคุณ',
  'earn.account.borrows': 'ยอดยืมของคุณ',
  'earn.account.borrowLimit': 'วงเงินยืม',
  'earn.account.liqThreshold': 'เกณฑ์การชำระบัญชี',
  'earn.account.health': 'สถานะบัญชี',
  'earn.account.depositedRow': 'ฝากแล้ว {amount} {symbol} (${usd})',
  'earn.account.borrowedRow': 'ยืมแล้ว {amount} {symbol} (${usd})',
  'earn.action.supply': 'ฝาก',
  'earn.action.borrow': 'ยืม',
  'earn.action.withdraw': 'ถอน',
  'earn.action.repay': 'ชำระคืน',
  'earn.actions.aria': 'การดำเนินการให้กู้ยืม',
  'earn.amount.label': 'จำนวน ({symbol})',
  'earn.amount.available': 'ใช้ได้ประมาณ {amount}',
  'earn.amount.max': 'สูงสุด',
  'earn.amount.allDebt': 'หนี้ทั้งหมด',
  'earn.submit.submitting': 'กำลังส่ง…',
  'earn.note.sameAssetBorrow': 'คุณยืม {symbol} ไม่ได้เนื่องจากเป็นสินทรัพย์หลักประกัน โปรดเลือกสินทรัพย์อื่นหรือถอนเงินฝากก่อน',
  'earn.note.overMax.withdraw': 'ถอนได้ประมาณ {amount} {symbol} เท่านั้น ส่วนที่เหลือต้องคงไว้เป็นหลักประกันของยอดยืมที่ยังค้างอยู่ โปรดชำระคืนก่อนหากต้องการถอนทั้งหมด',
  'earn.note.overMax.borrow': 'หลักประกันของคุณรองรับการยืมสูงสุดประมาณ {amount} {symbol}',
  'earn.note.overMax.repay': 'คุณมียอดค้างชำระประมาณ {amount} {symbol} เท่านั้น',
  'earn.note.noSigner': 'ดำเนินการไม่ได้: การลงนามทำผ่านเกตเวย์ แต่ไม่สามารถเชื่อมต่อทั้งเส้นทางให้กู้ยืมของเกตเวย์และตัวลงนามสำหรับการพัฒนาในเครื่อง',
  'earn.note.devSigner': 'ตัวลงนามสำหรับการพัฒนา (testnet): {address}',
  'earn.toast.confirmed': 'ยืนยัน{action}แล้ว ({digest}…)',
  'earn.toast.failedReason': '{action}ไม่สำเร็จ: {reason}',
  'earn.toast.failedOnChain': '{action}ไม่สำเร็จ: ข้อผิดพลาดบนเชน ({digest}…)',
  'earn.activity.title': 'กิจกรรมล่าสุด',

  // Filters and lists
  'filter.network.aria': 'ตัวกรองเครือข่าย',
  'filter.chain.aria': 'ตัวกรองบล็อกเชน',
  'filter.chain.selectNetwork': 'เลือกเครือข่าย',
  'holdings.empty': 'ยังไม่มีสินทรัพย์ที่นี่ โปรดรับสินทรัพย์หรือเปลี่ยนตัวกรองเพื่อดูเพิ่มเติม',
  'holdings.showHidden': 'แสดง {count} รายการที่ซ่อน · ต่ำกว่า $0.01',
  'holdings.hideSmall': 'ซ่อน {count} {word} มูลค่าน้อย',
  'holdings.balance': 'ยอดคงเหลือ',
  'holdings.balances': 'ยอดคงเหลือ',
  'chainSummary.empty': 'ยังไม่มีสินทรัพย์ที่นี่ โปรดรับสินทรัพย์เพื่อดูเครือข่ายของคุณ',
  'chainSummary.viewAria': 'ดู {name}, {count} {word}',
  'walletChain.empty': 'ยังไม่มีกระเป๋าเงินบนเครือข่ายนี้',
  'walletChain.openAria': 'เปิด {label}',

  // Account and wallet management sheets
  'account.closeSwitcher': 'ปิดตัวสลับกระเป๋าเงิน',
  'account.title': 'กระเป๋าเงินของคุณ',
  'account.allDesc': 'ทุกอย่างในรายการเดียว',
  'account.newWallet': 'กระเป๋าเงินใหม่',
  'account.searchPlaceholder': 'ค้นหาตามชื่อหรือที่อยู่',
  'account.searchAria': 'ค้นหากระเป๋าเงินตามชื่อหรือที่อยู่',
  'account.networks': 'เครือข่าย',
  'account.empty.filtered': 'ไม่มีกระเป๋าเงินที่ตรงกับตัวกรอง',
  'account.empty.none': 'ยังไม่มีกระเป๋าเงิน',
  'account.selectAria': 'เลือก {label}, {value}',
  'account.copyAddressAria': 'คัดลอกที่อยู่ {chain}',
  'account.showQrAria': 'แสดงคิวอาร์สำหรับรับบน {chain}',
  'selectNetwork.closeAria': 'ปิดตัวเลือกเครือข่าย',
  'selectNetwork.title': 'เลือกเครือข่าย',
  'selectNetwork.searchPlaceholder': 'ค้นหาเครือข่าย',
  'selectNetwork.searchAria': 'ค้นหาเครือข่ายตามชื่อ',
  'selectNetwork.groupAria': 'เครือข่าย',
  'selectNetwork.all': 'เครือข่ายทั้งหมด',
  'selectNetwork.empty': 'ไม่พบเครือข่ายที่ตรงกับการค้นหา',
  'walletTabs.sectionsAria': 'ส่วนต่างๆ ของกระเป๋าเงิน',
  'walletTabs.tokens': 'โทเค็น',
  'walletTabs.approvals': 'การอนุมัติ',
  'walletTabs.manage': 'จัดการคริปโท',
  'walletTabs.history': 'ประวัติ',
  'manage.closeAria': 'ปิดการจัดการคริปโท',
  'manage.aria': 'จัดการคริปโทสำหรับ {wallet}',
  'manage.title': 'จัดการคริปโท',
  'manage.searchPlaceholder': 'ค้นหาโทเค็น',
  'manage.searchAria': 'ค้นหาโทเค็นตามสัญลักษณ์หรือชื่อ',
  'manage.empty': 'กระเป๋าเงินนี้ยังไม่มีโทเค็น โปรดรับสินทรัพย์เพื่อจัดการที่นี่',
  'manage.noMatches': 'ไม่พบโทเค็นที่ตรงกับการค้นหา',
  'manage.tokensAria': 'โทเค็น',
  'manage.showTokenAria': 'แสดง {symbol} บน {network}',
  'manage.import': 'นำเข้าโทเค็นแบบกำหนดเอง',
  'manage.note': 'แสดงเฉพาะโทเค็นที่กระเป๋าเงินนี้ถืออยู่ นำเข้าโทเค็นด้วยที่อยู่สัญญาเพื่อติดตามสินทรัพย์แบบกำหนดเอง โทเค็นที่นำเข้าจะยังไม่ผ่านการตรวจสอบและไม่มีข้อมูลราคา',
  'import.closeAria': 'ปิดการนำเข้าโทเค็น',
  'import.aria': 'นำเข้าโทเค็นไปยัง {wallet}',
  'import.title': 'นำเข้าโทเค็น',
  'import.contract': 'ที่อยู่สัญญา',
  'import.contractPlaceholder': 'วางที่อยู่สัญญาของโทเค็น',
  'import.contractAria': 'ที่อยู่สัญญาของโทเค็น',
  'import.resolve': 'ตรวจสอบโทเค็น',
  'import.resolving': 'กำลังตรวจสอบ…',
  'import.unknownToken': 'โทเค็นที่ไม่รู้จัก',
  'import.unnamedToken': 'โทเค็นที่ไม่มีชื่อ',
  'import.decimals': 'ตำแหน่งทศนิยม',
  'import.balance': 'ยอดคงเหลือ',
  'import.hint': 'วางที่อยู่สัญญาของโทเค็นบน {network} แล้วตรวจสอบเพื่อดูตัวอย่างก่อนเพิ่ม',
  'import.warning': 'โทเค็นยังไม่ผ่านการตรวจสอบ โปรดตรวจสอบที่อยู่สัญญาด้วยตนเองก่อนเพิ่ม โทเค็นที่นำเข้าไม่อยู่ในรายการอนุญาตที่คัดสรรและไม่มีข้อมูลราคา',
  'import.add': 'เพิ่มโทเค็น',
  'import.error.cap': 'กระเป๋าเงินนี้มีโทเค็นที่นำเข้าถึงจำนวนสูงสุดแล้ว โปรดนำออกหนึ่งรายการก่อนเพิ่มรายการใหม่',

  // Wallet balance and wallet settings
  'walletBalance.tag.unverifiedTitle': 'โทเค็นนี้ไม่อยู่ในรายการอนุญาตที่คัดสรร',
  'walletBalance.testnetPillTitle': 'กระเป๋าเงิน Testnet ยอดคงเหลือไม่มีมูลค่าจริง',
  'walletBalance.testnet': 'Testnet',
  'walletBalance.loadingBalances': 'กำลังโหลดยอดคงเหลือ...',
  'walletBalance.statusPrefix': 'สถานะ: {status}',
  'walletBalance.keygen': 'กำลังสร้างคีย์ ยอดคงเหลือจะแสดงเมื่อพร้อม',
  'walletBalance.partial': 'อ่านยอดคงเหลือบางส่วนไม่ได้ ข้อมูลจึงอาจไม่ครบถ้วน',
  'walletBalance.chainFilterAria': 'ตัวกรองเครือข่าย',
  'walletBalance.testnetBanner': 'Testnet — ยอดคงเหลือที่แสดงเป็นยอดบน Testnet และไม่มีมูลค่าจริง ปิดโหมดนักพัฒนาในเมนูเพื่อใช้ Mainnet',
  'walletSettings.addressAria': 'ที่อยู่กระเป๋าเงิน',
  'walletSettings.detailsAria': 'รายละเอียดกระเป๋าเงิน',
  'walletSettings.address': 'ที่อยู่',
  'walletSettings.details': 'รายละเอียด',
  'walletSettings.addressPending': 'กำลังรอที่อยู่ ที่อยู่จะแสดงเมื่อคีย์ของกระเป๋าเงินเสร็จสมบูรณ์',
  'walletSettings.name': 'ชื่อ',
  'walletSettings.balance': 'ยอดคงเหลือ',
  'walletSettings.status': 'สถานะ',
  'walletSettings.curve': 'เส้นโค้ง',
  'walletSettings.publicKey': 'คีย์สาธารณะ',
  'walletSettings.shortAddress': 'ที่อยู่แบบย่อ',
  'walletSettings.pending': 'รอดำเนินการ',
  'walletSettings.unknownChain': 'เครือข่ายที่ไม่รู้จัก',

  // Welcome / language choice (first-visit screen)
  'welcome.eyebrow': 'ยินดีต้อนรับสู่',
  'welcome.tagline': 'ปกป้องสิ่งสำคัญของคุณ',
  'welcome.title': 'เลือกภาษาของคุณ',
  'welcome.cta': 'เริ่มต้นใช้งาน',
  'welcome.legalTitle': 'ประกาศทางกฎหมาย',
  'welcome.legalText': 'การสนับสนุนด้านภาษาจัดทำขึ้นโดยเป็นส่วนหนึ่งของความพยายามของเราในการเพิ่มการเข้าถึงสำหรับผู้ใช้ทั่วโลก บริการนี้มีให้เฉพาะในภูมิภาคที่กฎหมายอนุญาตให้ใช้งานในลักษณะดังกล่าว และมีวัตถุประสงค์เพียงเพื่ออำนวยความสะดวกแก่ผู้ใช้ที่ต้องการหรือจำเป็นต้องใช้การสนับสนุนด้านภาษาดังกล่าวเท่านั้น',
  'welcome.legalAgreement': 'การคลิกปุ่ม "ฉันยอมรับ" ด้านล่าง แสดงว่าคุณยืนยันว่าการใช้ภาษานี้เป็นไปตามกฎหมายและข้อบังคับท้องถิ่น',
  'welcome.agree': 'ฉันยอมรับ',

  'start.label': 'กระเป๋าเงิน',
  'start.points.badge': 'ขับเคลื่อนด้วย MPC',
  'start.points.lead': 'ไม่ต้องใช้คีย์ส่วนตัวหรือวลีกู้คืนที่มีความเสี่ยงสูงต่อการรั่วไหล',
  'start.points.shares':
    'กระเป๋าเงินของคุณได้รับการปกป้องด้วยองค์ประกอบความปลอดภัย (key shard) ที่กระจายอยู่ใน Google ข้อมูลสำรองของคุณ และระบบยืนยันตัวตนของอุปกรณ์',
  'start.points.twoRequired': 'การย้ายสินทรัพย์ต้องใช้องค์ประกอบความปลอดภัยอย่างน้อยสองรายการ',
  'start.points.neverWhole':
    'องค์ประกอบความปลอดภัยที่ปกป้องสินทรัพย์ของคุณจะไม่ถูกรวมกันหรือเก็บไว้ในที่เดียวโดยเด็ดขาด',
  'start.points.rules':
    'แม้องค์ประกอบความปลอดภัยจะมีปัญหา กฎความปลอดภัยเพิ่มเติมยังคงปกป้องสินทรัพย์ของคุณ',
  'start.points.more': 'อ่านเพิ่มเติม',
  'start.points.less': 'แสดงน้อยลง',
  'start.title': 'สร้างกระเป๋าเงินแบบไม่ระบุตัวตน',
  'start.desc': 'ปกป้องกระเป๋าเงินแบบกระจายอำนาจด้วยระบบยืนยันตัวตนของอุปกรณ์คุณ',
  'start.hint': 'ใบหน้า · ลายนิ้วมือ · พาสคีย์',
  'start.signin.prompt': 'มีบัญชีอยู่แล้วใช่ไหม',
  'start.signin.link': 'เข้าสู่ระบบ',
  'start.unsupported': 'เบราว์เซอร์นี้สร้างพาสคีย์ไม่ได้ โปรดเข้าสู่ระบบด้วยบัญชีจากผู้ให้บริการอื่นแทน',

  // Maintenance screen. Shown when the host answers /v1 with 503.
  'maintenance.title': 'กระเป๋าเงินอยู่ระหว่างบำรุงรักษา',
  'maintenance.body': 'เรากำลังดำเนินการบำรุงรักษาตามแผน เมื่อดำเนินการเสร็จแล้ว คุณจะกลับมาใช้กระเป๋าเงินได้อีกครั้ง',
  'maintenance.assurance': 'เงินและคีย์ของคุณยังคงปลอดภัย แม้คุณจะถูกออกจากระบบ สินทรัพย์ที่คุณถืออยู่ก็ไม่เปลี่ยนแปลง',
  'maintenance.retry': 'ตรวจสอบอีกครั้ง',
  'start.secondAccount.title': 'มีการลงทะเบียนวิธียืนยันตัวตนที่เชื่อมโยงกับบัญชีไว้ในอุปกรณ์นี้แล้ว',
  'start.secondAccount.body': 'ต้องการเพิ่มบัญชีอื่นหรือไม่',
  'start.secondAccount.signIn': 'ไปหน้าเข้าสู่ระบบ',
  'start.secondAccount.create': 'เพิ่มบัญชีอื่น',
};
